import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import { useTranslation } from 'react-i18next';
import { getBlogs } from '../api/blogApi';
const styles = `
  :root {
    --ft-accent:     #FF8139;
    --ft-accent-dim: rgba(226,74,74,0.12);
    --ft-accent-mid: rgba(226,74,74,0.25);
    --ft-bg:         #242424; 
    --ft-surface:    #2a2a2a;
    --ft-surface-2:  #333333;
    --ft-border:     rgba(255,255,255,0.08);
    --ft-border-md:  rgba(255,255,255,0.12);
    --ft-text:       rgba(255,255,255,0.85);
    --ft-muted:      rgba(255,255,255,0.5);
    --ft-grid:       rgba(255,255,255,0.04);
  }

  /* ════════════════════════════════════
     FOOTER SHELL
  ════════════════════════════════════ */
  .ft-footer {
    background: var(--ft-bg);
    font-family: 'DM Sans', sans-serif;
    position: relative;
    overflow: hidden;
  }

  /* Grid pattern */
  .ft-footer::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      linear-gradient(var(--ft-grid) 1px, transparent 1px),
      linear-gradient(90deg, var(--ft-grid) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none; z-index: 0;
  }

  /* Vignette */
  .ft-footer::after {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgba(36,36,36,0.85) 100%);
    pointer-events: none; z-index: 0;
  }

  /* Red blobs */
  .ft-blob-tl {
    position: absolute; top: -150px; left: -150px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(226,74,74,0.12) 0%, transparent 65%);
    pointer-events: none; z-index: 0;
  }

  .ft-blob-br {
    position: absolute; bottom: -100px; right: -100px;
    width: 350px; height: 350px; border-radius: 50%;
    background: radial-gradient(circle, rgba(226,74,74,0.08) 0%, transparent 65%);
    pointer-events: none; z-index: 0;
  }

  /* ════════════════════════════════════
     HERO BAND
  ════════════════════════════════════ */
  .ft-hero {
    position: relative; z-index: 1;
    border-bottom: 1px solid var(--ft-border-md);
    padding: 24px 0 20px; /* Reduced from 48px/36px */
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  .ft-big-tagline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(28px, 4vw, 48px); /* Reduced scale */
    letter-spacing: 2px;
    line-height: 0.95;
    color: #fff;
    margin: 0;
    position: relative;
  }

  .ft-big-tagline span { color: var(--ft-accent); }

  .ft-big-tagline .ft-ul {
    position: relative;
    display: inline-block;
  }

  .ft-big-tagline .ft-ul::after {
    content: '';
    position: absolute;
    bottom: 2px; left: 0; right: 0;
    height: 3px;
    background: var(--ft-accent);
    border-radius: 2px;
    opacity: 0.35;
  }

  .ft-hero-cta {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
    color: #fff; text-decoration: none;
    background: var(--ft-accent);
    border: 1px solid var(--ft-accent);
    padding: 10px 20px; border-radius: 6px; /* Tighter button */
    box-shadow: 0 4px 16px rgba(226,74,74,0.25);
    transition: box-shadow 0.25s, transform 0.18s;
 
    flex-shrink: 0;
  }

  .ft-hero-cta:hover {
    box-shadow: 0 6px 20px rgba(226,74,74,0.4);
    transform: translateY(-2px);
    color: #fff; text-decoration: none;
  }

  /* ════════════════════════════════════
     GRID COLUMNS
  ════════════════════════════════════ */
  .ft-grid {
    position: relative; z-index: 1;
    padding: 12px 0; /* Reduced from 48px */
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: 0;
    border-bottom: 1px solid var(--ft-border-md);
  }

  .ft-col {
    padding: 0 24px;
  }

  .ft-col:first-child { padding-left: 0; }
  .ft-col:last-child  { border-right: none; padding-right: 0; }

  .ft-col-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 13px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--ft-muted);
    margin-bottom: 12px; /* Reduced from 20px */
    display: flex; align-items: center; gap: 8px;
  }

  .ft-col-title::before {
    content: '';
    display: inline-block;
    width: 12px; height: 2px;
    background: var(--ft-accent);
    border-radius: 2px;
  }

  /* ── Brand col ── */
  .ft-logo { margin-bottom: 12px; display: block; }
  .ft-logo img { height: 120px; object-fit: contain; } /* Scaled down from 72px */

  .ft-tagline {
    font-size: 12px; line-height: 1.5;
    color: var(--ft-muted); margin-bottom: 16px;
    max-width: 260px;
  }

  .ft-social-label {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--ft-muted); margin-bottom: 8px;
  }

  .ft-socials { display: flex; gap: 6px; flex-wrap: wrap; }

  .ft-social-btn {
    width: 30px; height: 30px; border-radius: 50%; /* Smaller social icons */
    background: rgba(255,255,255,0.06);
    border: 1px solid var(--ft-border-md);
    display: flex; align-items: center; justify-content: center;
    color: var(--ft-text); font-size: 11px; text-decoration: none;
    transition: all 0.22s ease;
  }

  .ft-social-btn:hover {
    background: var(--ft-accent);
    border-color: var(--ft-accent);
    color: #fff; transform: translateY(-2px);
  }

  /* ── Blog posts col ── */
  .ft-blog-list { display: flex; flex-direction: column; gap: 0; }

  .ft-blog-item {
    padding: 1px 0; /* Reduced from 12px */
    border-bottom: 1px solid var(--ft-border);
  }

  .ft-blog-item:first-child { padding-top: 0; }
  .ft-blog-item:last-child  { border-bottom: none; padding-bottom: 0; }

  .ft-blog-date {
    font-size: 10px; letter-spacing: 1px; text-transform: uppercase;
    color: var(--ft-accent); margin-bottom: 2px;
    display: flex; align-items: center; gap: 6px;
  }

  .ft-blog-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 14px; letter-spacing: 0.5px; line-height: 1.2;
    margin: 0;
    font-weight: 200 !important;
  }

  .ft-blog-title a { color: var(--ft-text); text-decoration: none; transition: color 0.2s; }
  .ft-blog-title a:hover { color: var(--ft-accent); }

  /* ── Location col ── */
  .ft-location-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 15px; letter-spacing: 1px;
    color: #fff; margin-bottom: 4px; line-height: 1;
  }

  .ft-location-addr {
    font-size: 12px; line-height: 1.5;
    color: var(--ft-muted); margin-bottom: 12px;
  }

  .ft-map-link {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--ft-accent); text-decoration: none;
    border: 1px solid var(--ft-accent-mid);
    background: var(--ft-accent-dim);
    padding: 4px 10px; border-radius: 4px;
    transition: all 0.22s;
  }

  .ft-map-link:hover { background: var(--ft-accent); color: #fff; }

  /* ── Hours col ── */
  .ft-hours-list {
    list-style: none; padding: 0; margin: 0 0 12px;
    display: flex; flex-direction: column; gap: 0;
  }

  .ft-hours-item {
    display: flex; align-items: center; justify-content: space-between;
 
    border-bottom: 1px solid var(--ft-border);
    gap: 12px;
  }

  .ft-hours-item:last-child { border-bottom: none; }

  .ft-hours-day { font-size: 11px; color: var(--ft-muted); }

  .ft-hours-time {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 12px; letter-spacing: 1px; color: #fff;
  }

  .ft-hours-time a { color: inherit; text-decoration: none; transition: color 0.2s; }
  .ft-hours-time a:hover { color: var(--ft-accent); }

  .ft-hours-closed {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 10px; letter-spacing: 1px; text-transform: uppercase;
    color: var(--ft-accent); background: var(--ft-accent-dim);
    border: 1px solid var(--ft-accent-mid);
    padding: 2px 6px; border-radius: 4px;
  }

  .ft-schedule-link {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
    color: var(--ft-muted); text-decoration: none;
    transition: all 0.22s;
  }

  .ft-schedule-link:hover { color: var(--ft-accent); gap: 10px; }

  /* ════════════════════════════════════
     STATS BAND
  ════════════════════════════════════ */
  .ft-stats-band {
    position: relative; z-index: 1;
    display: grid; grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid var(--ft-border-md);
  }

  .ft-stat {
    padding: 12px 16px; /* Reduced from 20px */
    border-right: 1px solid var(--ft-border);
    display: flex; align-items: center; gap: 10px;
    transition: background 0.22s;
  }

  .ft-stat:last-child { border-right: none; }
  .ft-stat:hover { background: rgba(255,255,255,0.03); }

  .ft-stat-icon {
    width: 28px; height: 28px; border-radius: 6px; /* Scaled down icon */
    background: var(--ft-accent-dim); border: 1px solid var(--ft-accent-mid);
    display: flex; align-items: center; justify-content: center;
    color: var(--ft-accent); font-size: 12px; flex-shrink: 0;
  }

  .ft-stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 18px; letter-spacing: 1px; color: #fff; line-height: 1; margin-bottom: 2px;
  }

  .ft-stat-num span { color: var(--ft-accent); }
  .ft-stat-lbl { font-size: 10px; color: var(--ft-muted); letter-spacing: 0.5px; }

  /* ════════════════════════════════════
     BOTTOM BAR
  ════════════════════════════════════ */
  .ft-bottom {
    position: relative; z-index: 1;
    padding: 12px 0; /* Reduced from 16px */
    display: flex; align-items: center;
    justify-content: space-between;
    flex-wrap: wrap; gap: 10px;
  }

  .ft-copy { font-size: 11px; color: var(--ft-muted); display: flex; align-items: center; gap: 6px; }
  .ft-copy a { color: var(--ft-accent); text-decoration: none; font-weight: 500; transition: opacity 0.2s; }
  .ft-copy a:hover { opacity: 0.75; }
  .ft-copy i { font-size: 10px; color: var(--ft-accent); }

  .ft-bottom-links {
    list-style: none; padding: 0; margin: 0;
    display: flex; align-items: center; gap: 12px;
  }

  .ft-bottom-links li a { font-size: 10px; color: var(--ft-muted); text-decoration: none; letter-spacing: 0.5px; transition: color 0.22s; }
  .ft-bottom-links li a:hover { color: var(--ft-accent); }
  .ft-bottom-links li:not(:last-child) { border-right: 1px solid var(--ft-border); padding-right: 12px; }

  .ft-made-badge { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--ft-muted); }
  .ft-made-badge i { color: var(--ft-accent); font-size: 10px; }

  /* ════════════════════════════════════
     RESPONSIVE
  ════════════════════════════════════ */
  @media (max-width: 1100px) {
    .ft-grid { grid-template-columns: 1fr 1fr; gap: 0; padding: 17px 0; }
    .ft-col  { padding: 0 16px; border-right: none; border-bottom: 1px solid var(--ft-border); padding-bottom: 24px; margin-bottom: 24px; }
    .ft-col:nth-child(odd)  { padding-left: 0; border-right: 1px solid var(--ft-border); }
    .ft-col:nth-child(even) { padding-right: 0; }
    .ft-col:nth-last-child(-n+2) { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
    .ft-stats-band { grid-template-columns: repeat(2, 1fr); }
    .ft-stat:nth-child(2) { border-right: none; }
  }

  @media (max-width: 768px) {
    .ft-grid    { grid-template-columns: 1fr; }
    .ft-col     { padding: 0 0 16px !important; border-right: none !important; }
    .ft-col:nth-last-child(-n+2) { border-bottom: 1px solid var(--ft-border); margin-bottom: 16px; padding-bottom: 1px; }
    .ft-col:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
    .ft-hero    { flex-direction: column; align-items: flex-start; gap: 12px; padding: 20px 0 16px; }
    .ft-bottom  { flex-direction: column; align-items: center; text-align: center; }
  }

  @media (max-width: 480px) {
    .ft-stats-band { grid-template-columns: 1fr; }
    .ft-stat { border-right: none; border-bottom: 1px solid var(--ft-border); }
    .ft-stat:last-child { border-bottom: none; }
    .ft-bottom-links { gap: 8px; }
    .ft-bottom-links li:not(:last-child) { padding-right: 8px; }
  }
`;

const Footer2 = () => {
  const year = new Date().getFullYear();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  const [footerPosts, setFooterPosts] = useState([
    { slug: null, title: t('footer.blog.p1'), published_at: t('footer.blog.d1') },
    { slug: null, title: t('footer.blog.p2'), published_at: t('footer.blog.d2') },
    { slug: null, title: t('footer.blog.p3'), published_at: t('footer.blog.d3') },
  ]);

  useEffect(() => {
    getBlogs({ lang, page: 1, limit: 3 })
      .then(data => {
        if (data.success && data.data.posts.length > 0) {
          setFooterPosts(data.data.posts);
        }
      })
      .catch(() => { });
  }, [lang]);
  return (
    <>
      <style>{styles}</style>

      <footer className="ft-footer site-footer style-1 footer-action bg-dark">
        <div className="ft-blob-tl" />
        <div className="ft-blob-br" />

        <div className="container">

          {/* ── HERO BAND ── */}
          <div className="ft-hero">
            <h2 className="ft-big-tagline">
              Train Hard.<br />
              Live <span className="ft-ul">Strong</span>.
            </h2>
            <Link to="/contact-us" className="ft-hero-cta">
              {t('footer.hero.cta')}
              <i className={`fa-solid ${document?.documentElement?.dir === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'}`} />
            </Link>
          </div>

          {/* ── FOUR COLUMNS ── */}
          <div className="ft-grid footer-top">
            {/* Brand */}
            <div className="ft-col widget widget_about">
              <Link to="/" className="ft-logo footer-logo logo-white">
                <img
                  className="select_logo_white"
                  src={IMAGES.logoHeader}
                  alt="FitZone"
                  width="200"
                  loading="lazy"
                />
              </Link>
              <p className="ft-tagline footer-tagline">
                {t('footer.about.tagline')}
              </p>
              <p className="ft-social-label footer-social-label">{t('footer.about.followUs')}</p>
              <div className="ft-socials dz-social-icon style-1">
                {[
                  { href: 'https://www.tiktok.com/@fitness..secrets?_r=1&_t=ZS-95X01Ntqckf', icon: 'fab fa-tiktok', label: 'TikTok' },
                  { href: 'https://www.instagram.com/fitness.ssecrets?igsh=MTY3cnhydnpkNm9qeg%3D%3D&utm_source=qr', icon: 'fab fa-instagram', label: 'Instagram' },
                  { href: 'https://snapchat.com/t/478JCN9p', icon: 'fab fa-snapchat', label: 'Snapchat' },
                  { href: 'https://wa.me/966575755700', icon: 'fab fa-whatsapp', label: 'WhatsApp' },
                ].map((s, i) => (
                  <Link key={i} target="_blank" to={s.href} rel="noreferrer" title={s.label} className="ft-social-btn">
                    <i className={s.icon} />
                  </Link>
                ))}
              </div>
            </div>

            {/* Blog Posts */}
            <div className="ft-col widget recent-posts-entry">
              <h3 className="ft-col-title">{t('footer.blog.title')}</h3>
              <div className="ft-blog-list widget-post-bx">
                {footerPosts.map((post, i) => (
                  <div className="ft-blog-item widget-post clearfix" key={i}>
                    <div className="dz-info">
                      <div className="ft-blog-date">
                        <i className="fa-regular fa-calendar" />
                        {post.published_at}
                      </div>
                      <h6 className="ft-blog-title">
                        <Link to={post.slug ? `/blog-details/${post.slug}` : '/wellness-journal'}>
                          {post.title}
                        </Link>
                      </h6>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="ft-col widget widget_locations">
              <h3 className="ft-col-title">{t('footer.location.title')}</h3>
              <h6 className="ft-location-name footer-location-name">{t('footer.location.name')}</h6>
              <p className="ft-location-addr footer-location-address">
                2528 Al Kurnaysh Br Rd, Ash Shati, Jeddah 23415<br />
              </p>
              <a
                href="https://maps.app.goo.gl/hMeeYmbArGj74a1u5?g_st=iwb"
                target="_blank"
                rel="noreferrer"
                className="ft-map-link footer-map-badge"
              >
                {t('footer.location.viewOnMap')}
                <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 10 }} />
              </a>
            </div>

            {/* Hours */}
            <div className="ft-col widget widget_working">
              <h3 className="ft-col-title">{t('footer.workingHours.title')}</h3>
              <ul className="ft-hours-list">
                {[
                 { day: t('footer.workingHours.sunThur'), time: '07:00 – 23:00', closed: false },
                 { day: t('footer.workingHours.fri'), time: '16:00 – 23:00', closed: false },
                ].map((row, i) => (
                  <li className="ft-hours-item" key={i}>
                    <span className="ft-hours-day footer-hours-day">{row.day}</span>
                    {row.closed
                      ? <span className="ft-hours-closed">{t('footer.workingHours.closed')}</span>
                      : <span className="ft-hours-time footer-hours-time">
                        <Link to="/schedule">{row.time}</Link>
                      </span>
                    }
                  </li>
                ))}
              </ul>
              {/* <Link to="/schedule" className="ft-schedule-link footer-more-link">
                {t('footer.workingHours.fullSchedule')} <i className={`fa-solid ${document?.documentElement?.dir === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'}`} />
              </Link> */}
            </div>
          </div>

          {/* ── STATS BAND ── */}
          {/* <div className="ft-stats-band">
            {[
              { icon: 'fa-users', num: '12', suffix: 'K+', label: t('footer.stats.activeMembers') },
              { icon: 'fa-dumbbell', num: '50', suffix: '+', label: t('footer.stats.programsOffered') },
              { icon: 'fa-user-doctor', num: '15', suffix: '+', label: t('footer.stats.expertTrainers') },
              { icon: 'fa-trophy', num: '47', suffix: '+', label: t('footer.stats.awardsWon') },
            ].map((s, i) => (
              <div className="ft-stat" key={i}>
                <div className="ft-stat-icon">
                  <i className={`fa-solid ${s.icon}`} />
                </div>
                <div>
                  <div className="ft-stat-num">{s.num}<span>{s.suffix}</span></div>
                  <div className="ft-stat-lbl">{s.label}</div>
                </div>
              </div>
            ))}
          </div> */}

          {/* ── BOTTOM BAR ── */}
          <div className="ft-bottom footer-bottom footer-bottom-inner">
            <span className="ft-copy copyright-text footer-copy-text">
              <i className="fa-regular fa-copyright" />
              {year}{" "}
              <Link to="/" rel="noreferrer">Fitness Secrets</Link>.
              {" "}{t('footer.bottom.rights')}
            </span>

            <ul className="ft-bottom-links footer-bottom-links">
              <li><Link to="/privacy-policy">{t('footer.bottom.privacyPolicy')}</Link></li>
              <li><Link to="/terms-conditions">{t('footer.bottom.termsOfUse')}</Link></li>
            </ul>

            <span className="ft-made-badge">
              {t('footer.bottom.madeWith')} <i className="fa-solid fa-heart" /> {t('footer.bottom.in')}
            </span>
          </div>

        </div>
      </footer>
    </>
  );
};

export default Footer2;